import { ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, ReferenceLine, Label } from 'recharts';

export const performanceData = [
  {
    month: 'Dec 2022',
    fund: 11.84,
    'S&P 500': -5.76,
    'Vanguard Real Estate Index': -5.03,
    'EurekaHedge Hedge Fund Index': -0.06
  }, {
    month: 'Jan 2023',
    fund: 29.41,
    'S&P 500': 0.17,
    'Vanguard Real Estate Index': 4.85,
    'EurekaHedge Hedge Fund Index': 0.21
  }, {
    month: 'Feb 2023',
    fund: 35.80,
    'S&P 500': -2.35,
    'Vanguard Real Estate Index': -1.3,
    'EurekaHedge Hedge Fund Index': 0.87
  }, {
    month: 'Mar 2023',
    fund: 43.06,
    'S&P 500': 1.28,
    'Vanguard Real Estate Index': -3.45,
    'EurekaHedge Hedge Fund Index': 1.83
  }, {
    month: 'Apr 2023',
    fund: 45.11,
    'S&P 500': 2.89,
    'Vanguard Real Estate Index': -3.15,
    'EurekaHedge Hedge Fund Index': 1.07
  }, {
    month: 'May 2023',
    fund: 53.47,
    'S&P 500': 3.37,
    'Vanguard Real Estate Index': -6.98,
    'EurekaHedge Hedge Fund Index': 1.49
  }, {
    month: 'Jun 2023',
    fund: 65.78,
    'S&P 500': 10.07,
    'Vanguard Real Estate Index': -1.76,
    'EurekaHedge Hedge Fund Index': 0.76
  }, {
    month: 'Jul 2023',
    fund: 66.22,
    'S&P 500': 13.67,
    'Vanguard Real Estate Index': 0.24,
    'EurekaHedge Hedge Fund Index': 2.49
  }, {
    month: 'Aug 2023',
    fund: 65.27,
    'S&P 500': 11.82,
    'Vanguard Real Estate Index': -3.15,
    'EurekaHedge Hedge Fund Index': 2.57
  }, {
    month: 'Sep 2023',
    fund: 94.89,
    'S&P 500': 6.52,
    'Vanguard Real Estate Index': -10.19,
    'EurekaHedge Hedge Fund Index': 3.23
  }, {
    month: 'Oct 2023',
    fund: 101.69,
    'S&P 500': 4.21,
    'Vanguard Real Estate Index': -13.44,
    'EurekaHedge Hedge Fund Index': 4.29
  }, {
    month: 'Nov 2023',
    fund: 125.72,
    'S&P 500': 13.73,
    'Vanguard Real Estate Index': -2.99,
    'EurekaHedge Hedge Fund Index': 3.89
  }, {
    month: 'Dec 2023',
    fund: 130.77,
    'S&P 500': 13.8,
    'Vanguard Real Estate Index': 0.12,
    'EurekaHedge Hedge Fund Index': 3.89
  }
];

const finalValues = (() => {
  const lastItem = performanceData[performanceData.length - 1];
  return [
    { name: 'DEL PHI', value: `+${lastItem.fund.toFixed(2)}%`, color: '#ea384c' },
    { name: 'S&P 500', value: `${lastItem['S&P 500'] >= 0 ? '+' : ''}${lastItem['S&P 500'].toFixed(2)}%`, color: '#8884d8' },
    { name: 'Vanguard Real Estate', value: `${lastItem['Vanguard Real Estate Index'] >= 0 ? '+' : ''}${lastItem['Vanguard Real Estate Index'].toFixed(2)}%`, color: '#82ca9d' },
    { name: 'EurekaHedge HF Index', value: `${lastItem['EurekaHedge Hedge Fund Index'] >= 0 ? '+' : ''}${lastItem['EurekaHedge Hedge Fund Index'].toFixed(2)}%`, color: '#ffc658' }
  ];
})();

const PerformancePreview = () => {
  return <section className="py-20 bg-delphi-blue/10 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Performance Record</h2>
          <p className="text-delphi-light/70 text-lg">DEL(PHI)'s trading strategy has consistently delivered 99th percentile performance according to Barclay's Hedge Fund Index.</p>
        </div>
        
        <div className="bg-gradient-to-br from-delphi-red/20 via-delphi-red/10 to-transparent backdrop-blur-sm p-1 rounded-2xl">
          <div className="bg-delphi-dark border border-delphi-gray/20 rounded-xl p-6 mb-10 shadow-lg">
            <div className="mb-2">
              <h3 className="text-xl font-semibold flex items-center">
                <TrendingUp size={20} className="text-delphi-red mr-2" />
                <span>12-Month Cumulative Return (%)</span>
              </h3>
              <p className="text-delphi-light/60 text-sm mt-1">December 1st, 2022 to December 4th, 2023</p>
            </div>
            
            <div className="bg-delphi-gray/20 rounded-lg p-3 mb-4 inline-block">
              <div className="flex flex-wrap gap-4">
                {finalValues.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-3 h-3 rounded-sm mr-2" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm mr-1">{item.name}:</span>
                    <span className="text-sm font-bold" style={{ color: item.color }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="h-80 relative">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData} margin={{
                top: 5,
                right: 90,
                left: 0,
                bottom: 5
              }}>
                  <defs>
                    <pattern id="diagonalLines" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="4" stroke="#ea384c" strokeWidth="1" strokeOpacity="0.2" />
                    </pattern>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="month" stroke="#8E9196" />
                  <YAxis stroke="#8E9196" domain={[-15, 140]} />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1A1F2C',
                  borderColor: '#2a2a2a',
                  color: '#ffffff'
                }} />
                  <Area
                    type="monotone"
                    dataKey="fund"
                    stroke="none"
                    fill="url(#diagonalLines)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="fund" 
                    name="DEL PHI" 
                    stroke="#ea384c" 
                    strokeWidth={3} 
                    dot={{ r: 4 }} 
                    activeDot={{ r: 6 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="S&P 500" 
                    name="S&P 500" 
                    stroke="#8884d8" 
                    strokeWidth={2} 
                    dot={{ r: 3 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Vanguard Real Estate Index" 
                    name="Vanguard Real Estate Index" 
                    stroke="#82ca9d" 
                    strokeWidth={2} 
                    dot={{ r: 3 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="EurekaHedge Hedge Fund Index" 
                    name="EurekaHedge Hedge Fund Index" 
                    stroke="#ffc658" 
                    strokeWidth={2} 
                    dot={{ r: 3 }} 
                  />
                  
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <h3 className="text-2xl font-bold mb-2 text-delphi-red">130.77%</h3>
            <p className="text-delphi-light font-medium mb-2">Annual Return</p>
            <p className="text-delphi-light/60 text-sm">
              Significantly outperforming market benchmarks and peer hedge funds.
            </p>
          </div>
          
          <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <h3 className="text-2xl font-bold mb-2 text-delphi-red">1.21</h3>
            <p className="text-delphi-light font-medium mb-2">Sharpe Ratio</p>
            <p className="text-delphi-light/60 text-sm">
              Excellent risk-adjusted returns compared to volatility exposure.
            </p>
          </div>
          
          <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <h3 className="text-2xl font-bold mb-2 text-delphi-red">3.18</h3>
            <p className="text-delphi-light font-medium mb-2">Sortino Ratio</p>
            <p className="text-delphi-light/60 text-sm">
              Emphasizing a controlled use of Volatility, where a majority of the Volatility felt is to the upside.
            </p>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <Link to="/performance" className="inline-flex items-center px-6 py-3 border border-delphi-gray/30 rounded-md text-delphi-light hover:bg-delphi-gray/10 transition-all">
            <span>View Detailed Performance</span>
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>;
};
export default PerformancePreview;
